import type { MatomoConfig, MatomoInstance, MatomoTracker } from "./types";

/**
 * Matomo Analytics Service
 * Handles script loading and tracker initialization
 */
export class MatomoService {
	private static instance: MatomoService | null = null;
	private config: MatomoConfig;
	private tracker: MatomoTracker | null = null;
	private scriptLoaded = false;
	private scriptLoading = false;
	private loadPromise: Promise<void> | null = null;

	private constructor(config: MatomoConfig) {
		this.config = config;
		// Initialize the _paq array
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
		window._paq = window._paq || [];
	}

	public static getInstance(config?: MatomoConfig): MatomoService {
		if (!MatomoService.instance) {
			if (!config) {
				throw new Error("MatomoService requires config for initialization");
			}
			MatomoService.instance = new MatomoService(config);
		}
		return MatomoService.instance;
	}

	public static resetInstance(): void {
		MatomoService.instance = null;
	}

	/**
	 * Load Matomo tracking script
	 */
	public async loadScript(): Promise<void> {
		if (this.config.disabled) {
			return;
		}

		if (this.scriptLoaded) {
			return;
		}

		if (this.scriptLoading && this.loadPromise) {
			return this.loadPromise;
		}

		this.scriptLoading = true;

		this.loadPromise = new Promise<void>((resolve, reject) => {
			// Configure tracker before loading script
			const _paq = window._paq;

			// Set tracker URL and site ID
			_paq.push(["setTrackerUrl", this.getTrackerUrl()]);
			_paq.push(["setSiteId", this.config.siteId.toString()]);

			// Apply custom configurations
			if (this.config.configurations) {
				Object.entries(this.config.configurations).forEach(([key, value]) => {
					_paq.push([key, value]);
				});
			}

			// Configure heartbeat
			if (this.config.heartBeat?.active) {
				_paq.push([
					"enableHeartBeatTimer",
					this.config.heartBeat.seconds || 15,
				]);
			}

			// Configure link tracking
			if (this.config.linkTracking !== false) {
				_paq.push(["enableLinkTracking"]);
			}

			// Create and append script
			const script = document.createElement("script");
			script.type = "text/javascript";
			script.async = true;
			script.defer = true;
			script.src = this.getScriptUrl();

			script.onload = () => {
				this.scriptLoaded = true;
				this.scriptLoading = false;

				// Get tracker instance

				if ("Matomo" in window && "getTracker" in window.Matomo) {
					this.tracker = window.Matomo.getTracker(
						this.getTrackerUrl(),
						this.config.siteId
					);
				}

				resolve();
			};

			script.onerror = () => {
				this.scriptLoading = false;
				reject(new Error("Failed to load Matomo tracking script"));
			};

			// Append to head
			const firstScript = document.getElementsByTagName("script")[0];
			firstScript.parentNode?.insertBefore(script, firstScript);
		});

		return this.loadPromise;
	}

	/**
	 * Get the Matomo tracker instance
	 */
	public getTracker(): MatomoTracker | null {
		return this.tracker;
	}

	/**
	 * Get the _paq instance for direct access
	 */
	public getPaq(): MatomoInstance {
		return window._paq;
	}

	/**
	 * Push commands to _paq
	 */
	public push(args: Array<unknown>): void {
		if (!this.config.disabled) {
			window._paq.push(args);
		}
	}

	/**
	 * Check if tracking is enabled
	 */
	public isEnabled(): boolean {
		return !this.config.disabled;
	}

	/**
	 * Update configuration
	 */
	public updateConfig(config: Partial<MatomoConfig>): void {
		this.config = { ...this.config, ...config };
	}

	/**
	 * Get tracker URL
	 */
	private getTrackerUrl(): string {
		if (this.config.trackerUrl) {
			return this.config.trackerUrl;
		}
		return `${this.config.url}/matomo.php`;
	}

	/**
	 * Get script URL
	 */
	private getScriptUrl(): string {
		if (this.config.srcUrl) {
			return this.config.srcUrl;
		}
		return `${this.config.url}/matomo.js`;
	}

	/**
	 * Sanitize URL to remove sensitive data
	 * Can be customized based on your needs
	 */
	public static sanitizeUrl(url: string): string {
		try {
			const urlObject = new URL(url);

			// Remove sensitive query parameters
			const sensitiveParams = [
				"token",
				"auth",
				"key",
				"password",
				"secret",
				"session",
				"email",
			];

			sensitiveParams.forEach((parameter) => {
				urlObject.searchParams.delete(parameter);
			});

			// Remove numeric IDs from path (optional)
			// Example: /users/123/profile -> /users/ID/profile
			const sanitizedPath = urlObject.pathname.replace(/\/\d+\//g, "/ID/");
			urlObject.pathname = sanitizedPath;

			return urlObject.toString();
		} catch {
			// If URL parsing fails, return original
			return url;
		}
	}
}
